/**
 * Guardian console — the data-rights side of the DPDP consent chain.
 *
 * A guardian can see exactly what was consented to and when, export the whole
 * provable chain (purchase, adult verification evidence, declarations, consent
 * timestamp, audit hashes), and withdraw consent. Withdrawal is immediate: the
 * database gate closes on the next assessment write.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Download,
  Loader2,
  AlertTriangle,
  Clock,
  FileText,
  ArrowRight,

} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useConsentState, CONSENT_RETENTION_YEARS } from "@/lib/consentGate";

const PassportGuardian = () => {
  const { state, loading, refresh } = useConsentState();
  const [busy, setBusy] = useState<"export" | "withdraw" | null>(null);
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);

  const childName = state.childName?.trim() || "this learner";

  const invoke = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke("parental-consent", { body });
    if (error) throw new Error(error.message);
    return data as Record<string, unknown>;
  };

  const handleExport = async () => {
    setBusy("export");
    try {
      const data = await invoke({ action: "export" });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wekit-consent-record-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Consent record downloaded.");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const handleWithdraw = async () => {
    setBusy("withdraw");
    try {
      await invoke({ action: "withdraw", reason });
      await refresh();
      setConfirming(false);
      setReason("");
      toast.success("Consent withdrawn. No further assessment data will be processed.");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-live="polite">
        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">Loading the consent record</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-2xl space-y-6">
        <header className="space-y-2">
          <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
            Guardian console
          </p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            Consent &amp; data rights
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Everything we hold about the permission you gave, and what you can do with it.
          </p>
        </header>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
              Current status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Learner</span>
              <span className="font-medium text-foreground">{childName}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Age band</span>
              <span className="font-medium text-foreground">
                {state.age == null ? "Not recorded" : state.isMinor ? `${state.age} — minor` : `${state.age}`}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Adult verification</span>
              {state.verified ? (
                <Badge variant="secondary">
                  Verified · {state.verificationMethod === "otp_declaration" ? "one-time code" : "DigiLocker"}
                </Badge>
              ) : (
                <Badge variant="outline">Not verified</Badge>
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Parental consent</span>
              {state.consented ? (
                <Badge variant="secondary">Granted</Badge>
              ) : (
                <Badge variant="outline">Not granted</Badge>
              )}
            </div>
            {state.consentGrantedAt && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Granted at</span>
                <span className="font-medium text-foreground">
                  {new Date(state.consentGrantedAt).toLocaleString()}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Privacy notice</span>
              <span className="font-medium text-foreground">{state.noticeVersion ?? "—"}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Retention</span>
              <span className="font-medium text-foreground">{CONSENT_RETENTION_YEARS} years</span>
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/50">
              <span className="text-muted-foreground">Processing</span>
              {state.allowed ? (
                <Badge variant="secondary">Permitted</Badge>
              ) : (
                <Badge variant="destructive">Blocked</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {state.allowed && (
          <Card className="border-primary/30">
            <CardContent className="pt-6 pb-6 px-5 space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Everything's in place. {childName} can start their Career Passport whenever they're
                ready.
              </p>
              <Button asChild className="w-full h-12">
                <Link to="/start">
                  Continue the journey
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}



        {state.isMinor && !state.allowed && (
          <Card className="border-primary/30">
            <CardContent className="pt-6 pb-6 px-5 space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  No assessment data can be processed for {childName} yet. Finish the two consent
                  steps to open their Career Passport.
                </p>
              </div>
              <Button asChild className="w-full h-12">
                <Link to="/passport/consent">Verify &amp; give consent</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" aria-hidden="true" />
              Your rights
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Download the full record: the purchase, the adult verification evidence, each
                declaration you made, the consent timestamp and the tamper-evident audit hashes.
              </p>
              <Button variant="outline" className="w-full" onClick={handleExport} disabled={busy !== null}>
                {busy === "export" ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="h-4 w-4" aria-hidden="true" />
                )}
                Export consent record (JSON)
              </Button>
            </div>

            {state.consented && (
              <div className="space-y-3 pt-4 border-t border-border/50">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Withdrawing consent stops all further processing of {childName}'s assessment
                    data immediately. Results already produced stay available to you until you ask
                    us to erase them.
                  </p>
                </div>
                {confirming ? (
                  <div className="space-y-3">
                    <Textarea
                      placeholder="Reason (optional)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      maxLength={500}
                      aria-label="Reason for withdrawing consent"
                    />
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="destructive"
                        className="flex-1 h-12"
                        onClick={handleWithdraw}
                        disabled={busy !== null}
                      >
                        {busy === "withdraw" && (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        )}
                        Yes, withdraw consent
                      </Button>
                      <Button
                        variant="ghost"
                        className="flex-1 h-12"
                        onClick={() => setConfirming(false)}
                        disabled={busy !== null}
                      >
                        Keep it active
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full text-destructive"
                    onClick={() => setConfirming(true)}
                  >
                    Withdraw consent
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PassportGuardian;
